/**
 * How to show a dialog at a readable size on a zoomed-out page.
 *
 * The report is wider than a phone and has no initial-scale, so the browser
 * zooms the whole page out (iPhone: visualViewport.scale ~0.49, layout width
 * ~800). A fixed-position dialog is laid out in those zoomed-out pixels, so a
 * 520px dialog showed at ~250px with ~6px text (Jaron, Oct 30). Scaling the
 * dialog box by 1/scale puts it back at its natural size on the glass; the
 * width/height caps (in the box's own, unscaled pixels) keep it inside the
 * visible screen with a 12px margin on each side.
 *
 * Transform (not CSS zoom) on purpose: getBoundingClientRect returns the
 * transformed box, which is what the signature canvas uses to map touches.
 */
export type ModalFit = {
  maxHeight: number
  maxWidth: number
  scale: number
}

const MARGIN = 12

export function modalFit(
  viewport: { height: number; scale: number; width: number } | null | undefined,
): ModalFit | null {
  if (!viewport) return null
  const { height, scale, width } = viewport
  if (!(scale > 0) || !(width > 0) || !(height > 0)) return null
  // Not zoomed out (iPad, desktop, or the user pinched in): plain layout.
  const k = scale < 0.95 ? 1 / scale : 1
  // Visible area in the box's own pixels: layout px / k (= glass px when k = 1/scale).
  return {
    maxHeight: Math.max(0, Math.round(height / k) - 2 * MARGIN),
    maxWidth: Math.max(0, Math.round(width / k) - 2 * MARGIN),
    scale: k,
  }
}
